import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useChatbot } from '../contexts/ChatbotContext';
import { supabase } from '../lib/supabase';

interface ConversationStats {
  totalConversations: number;
  leadsCollected: number;
  averageDuration: string;
  conversionRate: number;
  conversationTrend: number;
  leadsCollectedTrend: number;
  durationTrend: number;
  conversionTrend: number;
}

interface PeriodStats {
  conversations: number;
  leads: number;
  avgDuration: number;
  conversions: number;
}

// Define types for database records
type ConversationRecord = {
  id: string;
  created_at: string;
  duration_seconds: number;
  message_count: number;
};

type ContactInfoRecord = {
  id: string;
  conversation_id: string;
  type: string;
  value: string;
  processed: boolean;
  created_at: string;
  updated_at: string;
};

export function useConversationStats() {
  const { chatbotPublicId, isLoading: isChatbotLoading } = useChatbot();
  const [stats, setStats] = useState<ConversationStats>({
    totalConversations: 0,
    leadsCollected: 0,
    averageDuration: '0m 0s',
    conversionRate: 0,
    conversationTrend: 0,
    leadsCollectedTrend: 0,
    durationTrend: 0,
    conversionTrend: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't fetch stats until chatbot is loaded
    if (isChatbotLoading || !chatbotPublicId) {
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        // Définir les périodes
        const now = new Date();
        const currentPeriodStart = startOfDay(subDays(now, 7));
        const previousPeriodStart = startOfDay(subDays(now, 14));

        // Récupérer les conversations pour les deux périodes using chatbotPublicId
        const { data: currentConversations, error: currentError } = await supabase
          .from('conversations')
          .select('id, created_at, duration_seconds, message_count')
          .eq('chatbot_public_id', chatbotPublicId)
          .gte('created_at', currentPeriodStart.toISOString())
          .lte('created_at', endOfDay(now).toISOString());

        if (currentError) throw currentError;

        const { data: previousConversations, error: previousError } = await supabase
          .from('conversations')
          .select('id, created_at, duration_seconds, message_count')
          .eq('chatbot_public_id', chatbotPublicId)
          .gte('created_at', previousPeriodStart.toISOString())
          .lt('created_at', currentPeriodStart.toISOString());

        if (previousError) throw previousError;

        // Récupérer les leads pour les conversations de la période actuelle
        const { data: currentLeads, error: currentLeadsError } = await supabase
          .from('conversation_contact_info')
          .select('*')
          .eq('chatbot_public_id', chatbotPublicId)
          .gte('created_at', currentPeriodStart.toISOString())
          .lte('created_at', endOfDay(now).toISOString());

        if (currentLeadsError) throw currentLeadsError;

        // Récupérer les leads pour les conversations de la période précédente
        const { data: previousLeads, error: previousLeadsError } = await supabase
          .from('conversation_contact_info')
          .select('*')
          .eq('chatbot_public_id', chatbotPublicId)
          .gte('created_at', previousPeriodStart.toISOString())
          .lt('created_at', currentPeriodStart.toISOString());

        if (previousLeadsError) throw previousLeadsError;

        // Calculer les statistiques pour chaque période
        const getCurrentStats = (conversations: ConversationRecord[], leads: ContactInfoRecord[]): PeriodStats => ({
          conversations: conversations?.length || 0,
          leads: leads?.length || 0,
          avgDuration: conversations?.length ? 
            conversations.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) / conversations.length : 
            0,
          conversions: leads?.length || 0
        });

        const currentStats = getCurrentStats(currentConversations || [], currentLeads || []);
        const previousStats = getCurrentStats(previousConversations || [], previousLeads || []);

        // Calculer les tendances
        const calculateTrend = (current: number, previous: number): number => {
          if (previous === 0) return current > 0 ? 100 : 0;
          return Math.round(((current - previous) / previous) * 100);
        };

        // Formater la durée moyenne
        const minutes = Math.floor(currentStats.avgDuration / 60);
        const seconds = Math.round(currentStats.avgDuration % 60);

        // Calculer le taux de conversion actuel
        const conversionRate = currentStats.conversations > 0
          ? Math.round((currentStats.conversions / currentStats.conversations) * 100)
          : 0;

        // Calculer le taux de conversion précédent
        const previousConversionRate = previousStats.conversations > 0
          ? Math.round((previousStats.conversions / previousStats.conversations) * 100)
          : 0;

        setStats({
          totalConversations: currentStats.conversations,
          leadsCollected: currentStats.leads,
          averageDuration: `${minutes}m ${seconds}s`,
          conversionRate,
          conversationTrend: calculateTrend(currentStats.conversations, previousStats.conversations),
          leadsCollectedTrend: calculateTrend(currentStats.leads, previousStats.leads),
          durationTrend: calculateTrend(currentStats.avgDuration, previousStats.avgDuration),
          conversionTrend: calculateTrend(conversionRate, previousConversionRate)
        });
      } catch (err) {
        console.error('Error fetching conversation stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch conversation stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Mettre à jour toutes les 5 minutes
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [chatbotPublicId, isChatbotLoading]);

  return { stats, loading, error };
}